/*
 * @FileName       : report.service.ts
 * @Version        : 1.0
 * @DateOfCreation : 04/17/2020
 * @Author         : $u^^!t @ FFF Enterprises
 * @Dependencies   : N/A
 * @Description    : Report Service to perform all reports data call.
 *
 * Copyright (c) 2020, FFF Enterprises. All rights reserved.
 */

import { TenantsFilter, TenantsList } from '../models/tenants/tenants';
import { TENANTS, NOTIFICATIONS } from '../constants/api.url';
import { HttpResponse, ResultSet } from '../models/common/result.set';
import Api from '../lib/api';

export class TenantsService {
	private static instance: TenantsService;
	private constructor() {}

	public static getInstance(): TenantsService {
		if (!TenantsService.instance) {
			TenantsService.instance = new TenantsService();
		}
		return TenantsService.instance;
	}

	public tenantsList = async (filter: TenantsFilter): Promise<HttpResponse<ResultSet<TenantsList>>> => {
		return await Api.post(TENANTS.LIST, filter);
	};

	public getUserTenants = async (location: any): Promise<HttpResponse<ResultSet<TenantsList>>> => {
		const response = await Api.post(TENANTS.LIST, { limit: 100000 }); //get all on app start

		const customerId = location.pathname.split('/')[2];
		if (!customerId) {
			return response;
		}
		const tenantList = response.data.result;
		const loadedCustomer = tenantList.find((tenant: any) => tenant.customerId === customerId);
		if (!loadedCustomer) {
			try {
				const missingTenant = await Api.post(TENANTS.LIST, {
					where: {
						customerId: customerId,
					},
					limit: 1,
				});
				response.data.result = tenantList.concat({
					id: missingTenant.data.result[0].id,
					customerId: missingTenant.data.result[0].customerId,
					name: missingTenant.data.result[0].name,
				});
			} catch (err) {
				console.error('Error in getUserTenants', err);
				// continue if error occurs
			}
		}
		return response;
	};

	public tenantsAdditionalInfo = async (customerId: any | null): Promise<HttpResponse<ResultSet<TenantsList>>> => {
		return await Api.get(`${TENANTS.ADDITIONAL_INFO}/${customerId}`);
	};

	public searchTenant = async (customerId: string | null = null): Promise<HttpResponse<ResultSet<TenantsList>>> => {
		const response = await Api.get(TENANTS.GET);
		if (!customerId) {
			return response;
		}
		const tenantList = response.data.result;
		const currentTenants = tenantList.find((tenant: any) => tenant.customerId === customerId);
		if (!currentTenants) {
			try {
				const missingTenantData = (
					await Api.post(TENANTS.LIST, {
						where: {
							customerId: customerId,
						},
						limit: 1,
					})
				).data.result[0];
				response.data.result = tenantList.concat({
					id: missingTenantData.id,
					customerId: missingTenantData.customerId,
					name: missingTenantData.name,
				});
				response.data.total_record++;
			} catch (err) {
				// continue if error occurs
			}
		}
		return response;
	};

	public getNotificationActivity = async (customerId: string): Promise<any> => {
		const { data } = await Api.get(NOTIFICATIONS.LIST, [
			{ key: 'customerId', value: customerId },
			{ key: 'limit', value: 1000 },
		]);
		return data.messages;
	};

	public getNotificationMessage = async (messageId: string): Promise<any> => {
		const { data } = await Api.get(NOTIFICATIONS.GET + '/' + messageId);
		return data;
	};

	public syncCustomer = async (payload: any): Promise<any> =>
		await Api.get(`${TENANTS.GET}/sync/${payload.customerId}`, [
			{ key: 'customerId', value: payload.customerId },
			{ key: 'igCustomer', value: payload.igCustomer },
		]);

	public updateCustomer = async (payload: any): Promise<any> =>
		await Api.patch(`${TENANTS.UPDATE}/${payload.requestBody.id}`, payload.requestBody);

	public getTenantProducts = async (filter: string): Promise<HttpResponse<any>> =>
		await Api.get(`${TENANTS.PRODUCTS}`, [{ key: 'filter', value: filter }]);

	public createCustomerProduct = async (payload: any): Promise<HttpResponse<any>> =>
		await Api.post(`${TENANTS.PRODUCTS}`, payload);

	//calls Par service and asks for list of products for which this customer is allowed to pay invoices via CC
	public getCreditCardEligibleProductsForCustomer = async (customerId: string): Promise<any> => {
		let res;
		const filter = { where: { and: [{ customerId: customerId }, { isCCPayable: true }] } };

		res = await Api.get(`${TENANTS.PAR}?filter=${JSON.stringify(filter)}`);
		return res;
	};

	public getClassOfTradeOptions = async (): Promise<HttpResponse<ResultSet<any>>> => {
		return await Api.get(`${TENANTS.CLASS_OF_TRADE_OPTIONS}`)
	}

	public getFacilityTypeOptions = async (): Promise<HttpResponse<ResultSet<any>>> => {
	return await Api.get(`${TENANTS.FACILITY_TYPE_OPTIONS}`)
	}
}
